/* tslint:disable:no-console */
import Utils from "platform/util/Utils";
import {Environment} from "platform/enum/Environment";
import {IntegrationLog} from "platform/integration/message/IntegrationLog";
import WebUtil from "platform/util/WebUtil";
import {IntegrationMessage} from "platform/integration/message/IntegrationMessage";

enum LogLevel {

    DISABLED = "disabled",
    ERROR = "error",
    WARNING = "warn",
    INFO = "info",
    DEBUG = "debug",
}

const LogLevelPriority = {};
LogLevelPriority[LogLevel.DEBUG] = 0;
LogLevelPriority[LogLevel.INFO] = 1;
LogLevelPriority[LogLevel.WARNING] = 2;
LogLevelPriority[LogLevel.ERROR] = 3;
LogLevelPriority[LogLevel.DISABLED] = 4;

namespace LogLevel {
    export function deserialize(logLevel: string): LogLevel {
        if (Utils.isNotEmpty(logLevel)) {
            switch (logLevel) {
                case "disabled":
                    return LogLevel.DISABLED;
                case "error":
                    return LogLevel.ERROR;
                case "warn":
                    return LogLevel.WARNING;
                case "info":
                    return LogLevel.INFO;
                case "debug":
                    return LogLevel.DEBUG;
            }
        }
        return null;
    }
}

declare global {
    interface Window {
        Sentry: {
            captureException: (Error: Error) => void;
        };
    }
}

const PostMessageLog: boolean = WebUtil.urlParam("pmLog") === "true";
const UID: string = WebUtil.urlParam("uid");

class Logger {

    private static _LOG_LEVEL: LogLevel = LogLevel.DEBUG;
    private static _allLogs: string[] = [];
    private name: string;
    private readonly sentry: any;

    private constructor(name: string) {
        this.name = name;
        if (window.Sentry) {
            this.sentry = window.Sentry;
        }
    }

    public static Of(name: string): Logger {
        return new Logger(name);
    }

    public static AllLogs(): string[] {
        return this._allLogs;
    }

    public static setLogLevel(logLevel: string): void {
        Logger.SetLogLevel(LogLevel.deserialize(logLevel));
    }

    public static SetLogLevel(logLevel: LogLevel): void {
        if (Utils.isNotNull(logLevel)) {
            Logger._LOG_LEVEL = logLevel;
        }
    }

    public static getLogLevel(): LogLevel {
        return this._LOG_LEVEL;
    }

    public debug(message?: any, ...optionalParams: any[]): void {
        this.log(LogLevel.DEBUG, " D ", message, optionalParams);
    }

    public info(message?: any, ...optionalParams: any[]): void {
        this.log(LogLevel.INFO, " I ", message, optionalParams);
    }

    public warn(message?: any, ...optionalParams: any[]): void {
        this.log(LogLevel.WARNING, " W ", message, optionalParams);
    }

    public error(message?: any, ...optionalParams: any[]): void {
        this.log(LogLevel.ERROR, " E ", message, optionalParams);
    }

    private log(level: LogLevel, sLevel: string, message: any, optionalParams: any[]): void {
        if (LogLevelPriority[level] >= LogLevelPriority[Logger._LOG_LEVEL]) {
            if (Utils.isArrayNotEmpty(optionalParams)) {
                console[level](this.format(sLevel), message, JSON.stringify(optionalParams));
            } else {
                console[level](this.format(sLevel), message);
            }
            if (!Environment.isProd()) {
                Logger._allLogs.push(message);
                if (PostMessageLog && (window as any).ReactNativeWebView) {
                    const log: IntegrationMessage = new IntegrationLog(this.format(sLevel) + message);
                    log.uid = UID;
                    (window as any).ReactNativeWebView.postMessage(JSON.stringify(log));
                }
            }
        }
    }

    private format(level: string): string {
        return [Logger.formatDate(), level, "[", this.name, "]", " "].join("");
    }

    private static formatDate(): string {
        const date: Date = new Date();
        return [Utils.pad(date.getDate()), Utils.pad(date.getMonth() + 1), date.getFullYear()].join("-") + " "
            + [Utils.pad(date.getHours()), Utils.pad(date.getMinutes()), Utils.pad(date.getSeconds())].join(":") + "." + date.getMilliseconds();
    }

    public errorSentry(message?: any, ...optionalParams: any[]): void {
        this.createSentryException(message, optionalParams);
        this.error(message, optionalParams);
    }

    private createSentryException(message?: any, optionalParams: any[] = []): void {
        if (this.sentry) {
            this.sentry.captureException(new Error(JSON.stringify({message, ...optionalParams})));
        }
    }
}

export {LogLevel, Logger};
